import { z } from 'zod'

const healthStateSchema = z.enum(['READY', 'EMPTY', 'INVALID'])
const syncStateSchema = z.enum(['GREEN', 'RED', 'UNKNOWN'])

export const schedulingIssueSchema = z.enum([
  'SNAPSHOT_REQUIRED',
  'PROVIDERS_EMPTY',
  'APPOINTMENT_TYPES_EMPTY',
  'AVAILABILITIES_EMPTY',
  'PROVIDER_TYPE_ASSOCIATIONS_EMPTY',
  'SYNC_READ_NOT_GREEN',
  'SYNC_WRITE_NOT_GREEN',
  'DEFAULT_PROVIDER_REQUIRED',
  'BOOKING_POLICY_ENFORCEMENT_NOT_READY',
  'PROFILE_INVALID',
  'VALIDATION_REQUIRED',
  'INVALID_APPOINTMENT_TYPE',
  'INVALID_PROVIDER_ASSOCIATION',
  'INVALID_AVAILABILITY',
  'INVALID_WORKING_HOURS',
  'VENDOR_UNAVAILABLE',
  'SCHEDULING_PAYLOAD_INVALID',
])

const providerSchema = z
  .object({
    id: z.number().int().positive(),
    name: z.string().trim().min(1).max(200),
    usable: z.boolean(),
  })
  .strict()

const appointmentTypeSchema = z
  .object({
    id: z.number().int().positive(),
    name: z.string().trim().min(1).max(200),
    minutes: z.number().int().positive().max(1_440),
    bookableOnline: z.boolean().nullable(),
    providerIds: z.array(z.number().int().positive()).max(50),
  })
  .strict()

const operatorySchema = z
  .object({
    id: z.number().int().positive(),
    name: z.string().trim().min(1).max(200),
  })
  .strict()

const resourceHealthSchema = z
  .object({
    providers: healthStateSchema,
    appointmentTypes: healthStateSchema,
    availabilities: healthStateSchema,
    operatories: z.enum(['READY', 'OPTIONAL_EMPTY', 'INVALID']),
    providerTypeAssociations: healthStateSchema,
    syncRead: syncStateSchema,
    syncWrite: syncStateSchema,
  })
  .strict()

const schedulingSnapshotSchema = z
  .object({
    providers: z.array(providerSchema).max(300),
    appointmentTypes: z.array(appointmentTypeSchema).max(300),
    operatories: z.array(operatorySchema).max(300),
    availabilityCount: z.number().int().nonnegative().max(300),
    resourceHealth: resourceHealthSchema,
    syncStatus: z
      .object({ read: syncStateSchema, write: syncStateSchema })
      .strict(),
    discoveredAtMs: z.number().int().nonnegative(),
  })
  .strict()

const schedulingValidationSchema = z.discriminatedUnion('status', [
  z
    .object({
      status: z.literal('NOT_RUN'),
      issues: z.array(schedulingIssueSchema).max(20),
    })
    .strict(),
  z
    .object({
      status: z.enum([
        'VALID_QUERY_NO_SLOTS',
        'VALID_QUERY_WITH_SLOTS',
        'INVALID_CONFIGURATION',
        'VENDOR_FAILURE',
      ]),
      issues: z.array(schedulingIssueSchema).max(20),
      lastValidatedAtMs: z.number().int().nonnegative(),
    })
    .strict(),
])

const legacySchedulingResponseSchema = z
  .object({
    schemaVersion: z.literal(1),
    revision: z.number().int().positive(),
    discoveryRevision: z.number().int().nonnegative(),
    selectedLocationName: z.string().trim().min(1).max(200),
    bookingConsent: z.object({ newAppointment: z.boolean() }).strict(),
    bookingPolicyEnforcement: z
      .object({ newAppointment: z.literal('READY') })
      .strict(),
    validation: schedulingValidationSchema,
    completionReady: z.boolean(),
    blockingIssues: z.array(schedulingIssueSchema).max(20),
    defaultProviderId: z.number().int().positive().optional(),
    lastDiscoveryFailure: z
      .object({
        code: z.enum(['VENDOR_UNAVAILABLE', 'SCHEDULING_PAYLOAD_INVALID']),
        occurredAtMs: z.number().int().nonnegative(),
      })
      .strict()
      .optional(),
    snapshot: schedulingSnapshotSchema.optional(),
  })
  .strict()

const pav1SchedulingResponseSchema = z
  .object({
    schemaVersion: z.literal(2),
    authorityMode: z.literal('PROVIDER_ACCOUNT_V1'),
    revision: z.number().int().positive(),
    bookingConsent: z.object({ newAppointment: z.literal(false) }).strict(),
    configurationStatus: z.literal('NOT_CONFIGURED'),
    resourceStatus: z.enum(['READY', 'REFRESHING']),
    resourceRevision: z.number().int().positive().nullable(),
    resourceObservationRevision: z.number().int().positive().nullable(),
    syncReadStatus: syncStateSchema,
    syncWriteStatus: syncStateSchema,
    completionReady: z.boolean(),
    liveBookingEnabled: z.literal(false),
  })
  .strict()

export const schedulingResponseSchema = z.discriminatedUnion('schemaVersion', [
  legacySchedulingResponseSchema,
  pav1SchedulingResponseSchema,
])

export type LegacySchedulingResponse = z.infer<
  typeof legacySchedulingResponseSchema
>
export type SchedulingResponse = z.infer<typeof schedulingResponseSchema>

export function hasHealthySchedulingSnapshot(
  response: SchedulingResponse
): boolean {
  if (response.schemaVersion !== 1) return false
  const health = response.snapshot?.resourceHealth
  return Boolean(
    health &&
    health.providers === 'READY' &&
    health.appointmentTypes === 'READY' &&
    health.availabilities === 'READY' &&
    health.providerTypeAssociations === 'READY'
  )
}
